import { CONFIRMATION_TYPES } from '../../enums/confirmation-types.enum';
import { LoadStatus } from '../load-status';
import { NotificationIdState } from '../utilities/notification-id.type';
import { ConfirmationComplaintsState } from './sections/confirmation-complaints.state';
import { ConfirmationEquipmentState } from './sections/confirmation-equipment.state';
import { ConfirmationExpensesState } from './sections/confirmation-expenses.state';
import { ConfirmationFollowupState } from './sections/confirmation-followup.state';
import { ConfirmationInstallationPaperworkState } from './sections/confirmation-installation-paperwork.state';
import { ConfirmationMaterialsState } from './sections/confirmation-materials.state';
import { ConfirmationMeasurementToolsState } from './sections/confirmation-measurement-tools.state';
import { ConfirmationMeasuringPointsState } from './sections/confirmation-measuring-points.state';
import { ConfirmationParticipantsState } from './sections/confirmation-participants.state';
import { ConfirmationReasonMissingProtocolsState } from './sections/confirmation-reason-missing-protocols.state';
import { ConfirmationServiceReportState } from './sections/confirmation-service-report.state';
import { ConfirmationSourceReplacementsState } from './sections/confirmation-source-replacements.state';
import { ConfirmationTimeRecordingState } from './sections/confirmation-time-recording.state';
import { ConfirmationUploadToDocboxState } from './sections/confirmation-upload-to-docbox.state';

/**
 * Feature key under which the confirmation slice is registered in the root store (mirrors
 * `timesheetCollection`).
 */
export const CONFIRMATION_COLLECTION_FEATURE_KEY = 'confirmationCollection';

/**
 * Which stored confirmation an action payload is about, and — spread into {@link ConfirmationState} —
 * the confirmation's own identity, so a payload and the entry it addresses name their identity the
 * same way. The type field cannot be called `type` — NgRx reserves that name for the action's own
 * discriminator — hence `confirmationType`, the name `cleanupCompletedPreviously` already used for
 * the pair.
 */
export type ConfirmationIdentityState = Readonly<{
    notificationId: NotificationIdState;
    confirmationType: CONFIRMATION_TYPES;
}>;

/**
 * Serializable confirmation slice.
 *
 * `confirmations` is the persisted projection: the generic persistence layer keeps one
 * concurrency-store entry per confirmation, so devices editing different confirmations merge instead
 * of overwriting each other. `status` is transient runtime state and is not persisted.
 *
 * The submit pipeline's outcome is deliberately *not* kept here: `ConfirmationNgrxService`
 * awaits `submitConfirmationSucceeded`/`Failed` on the action stream, correlated by
 * `correlationId`, so storing the last outcome in the slice would be state nothing reads.
 */
export type ConfirmationCollectionState = Readonly<{
    confirmations: ConfirmationState[];
    status: LoadStatus;
    // TODO add error and persist the error
}>;

export const initialConfirmationCollectionState: ConfirmationCollectionState = {
    confirmations: [],
    status: LoadStatus.Idle,
};

/**
 * One stored confirmation: the technician's business data, section by section, and the plain-data
 * projection of the legacy `ConfirmationModel` — the slice never imports or instantiates the model
 * class itself. It is what the collection holds, addressed by the {@link ConfirmationIdentityState}
 * it carries (`isConfirmationFor` in `utility/confirmation-utilities.ts` does the comparing) and
 * ordered by its `saveTimestamp`, which the persistence layer stamps on the stored entry.
 *
 * It is not the legacy `JsonConfirmationModel` — the shapes differ in two ways:
 *
 * - `confirmationType` is a real field here. `ConfirmationModel.toJSON()` never emitted the type, so
 *   `modelToConfirmationState` stamps it; it is what identifies the confirmation alongside its notification.
 * - there is no `accordionOpenedStatus`. The model keeps the thirteen accordion flags in one object; here
 *   each one sits on the section it describes (`AccordionOpenedState` in `sections/accordion-opened.state.ts`),
 *   so a stored section carries everything the slice knows about it. The converters split that object up on
 *   save and rebuild it on load.
 *
 * Every section is business data plus that one flag; the converters in `confirmation-section.converters.ts`
 * are the only place that still names the legacy JSON shapes, and only to widen back into them on load.
 */
export type ConfirmationState = ConfirmationIdentityState &
    Readonly<{
        // Sections are optional because the model only builds the ones config and confirmation type allow
        // — a short confirmation has no docbox section, a notification without complaints has no
        // questionnaire. The legacy `Json*` interfaces declare them required, which was never true.
        equipment?: ConfirmationEquipmentState;
        uploadToDocBox?: ConfirmationUploadToDocboxState;
        complaints?: ConfirmationComplaintsState;
        followup?: ConfirmationFollowupState;
        measuringPoints?: ConfirmationMeasuringPointsState;
        participants?: ConfirmationParticipantsState;
        sourceReplacements?: ConfirmationSourceReplacementsState;
        expenses?: ConfirmationExpensesState;
        serviceReport?: ConfirmationServiceReportState;
        measurementTool?: ConfirmationMeasurementToolsState;
        timeRecording?: ConfirmationTimeRecordingState;
        installationPaperwork?: ConfirmationInstallationPaperworkState;
        // Absent until the technician documents a reason, and whenever config does not ask for one.
        reasonMissingProtocols?: ConfirmationReasonMissingProtocolsState;
        // Not optional, unlike the other sections: it holds no business data whose absence would mean
        // "no section", only the accordion flag, and the model always has one of those.
        materials: ConfirmationMaterialsState;
        saveTimestamp: number;
    }>;

// Note: this slice intentionally has no `createDefaultConfirmation()` state-layer factory. Unlike
// notification-status/timesheet entries (a handful of scalar flags), a confirmation only ever exists
// once fully formed — it is always constructed from a legacy `ConfirmationModel` via
// `modelToConfirmationState()` (in `services/confirmation-model.converters.ts`, which the slice may
// not import). There is no create-if-absent branch in the reducer: `upsertConfirmation` always
// carries a complete confirmation, and there is no scalar-field mutation action that would need to
// synthesize a placeholder one.
